/** Shared control for the Jira-request actions (APP_BUILD_ANDROID / APP_BUILD_IOS /
 *  PUSH_CERT_REQUEST): file (or re-file) a Jira ticket assigned to the responsible developer(s) via
 *  the execute endpoint, and link to it. The ticket is deduped server-side by a per-operator label.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { ConsoleApiError } from '@/api/client';
import { items } from '@/api/preflight';
import type { JiraRequestDetail, PreflightItem } from '@/api/preflight';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { copyOnlyActions } from '../tenantLinks';

export function JiraRequestControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const detail = item.detail as unknown as JiraRequestDetail;
  const jira = detail?.jira ?? null;

  const file = useMutation({
    mutationFn: () => items.execute(topId, item.id),
    onSuccess: (res) => {
      const run = res.run;
      if (run && run.status === 'error') {
        snackbar.show(run.error_message || 'Could not file the request.', 'error');
      } else {
        snackbar.show(run?.summary || 'Request filed.', 'success');
      }
      onChanged();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not file the request.', 'error'),
  });

  if (detail?.configured === false) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          Jira isn&apos;t configured on the server, so the request can&apos;t be filed automatically.
        </Alert>
      </Box>
    );
  }

  const assignees = detail?.assignees ?? [];
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {jira?.url ? (
            <CheckCircleIcon fontSize="small" color="success" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
          )}
          <Typography variant="body2" component="span">
            {jira?.url ? (
              <>
                Ticket filed:{' '}
                <Link href={jira.url} target="_blank" rel="noopener">
                  {jira.key || jira.url}
                </Link>
                {jira.key && (
                  <>
                    {' '}
                    {/* The key is what goes in a Slack message or a standup note. */}
                    <ValueActions actions={copyOnlyActions(jira.key, 'Copy ticket key')}>
                      (copy)
                    </ValueActions>
                  </>
                )}
              </>
            ) : (
              'No Jira ticket yet'
            )}
          </Typography>
        </Stack>
        {assignees.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            Assigned to: {assignees.join(', ')}
          </Typography>
        )}
        {canManage && (
          <Box>
            <Button
              size="small"
              variant="contained"
              onClick={() => file.mutate()}
              disabled={file.isPending}
              startIcon={file.isPending ? <CircularProgress size={14} /> : undefined}
            >
              {jira?.url ? 'Re-check / re-file ticket' : 'File Jira request'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
